import type { MapModel } from '../engine/map/mapModel';
import type { HexMapState } from '../engine/render/renderer';
import { createBattleState, advanceBattle, buildBattleReport } from '../engine/battle/battleEngine';
import { buildUnitRoster } from '../engine/battle/roster';
import { applyPopulationLoss, addInventory } from '../engine/map/settlement';
import { getScoutingTravelTicks } from '../engine/scouting/scouting';
import { hexDistance } from '../engine/hex/hexMath';
import type { Tile } from '../engine/hex/types';

export function runScoutingMissions(model: MapModel, state: HexMapState): boolean {
    if (state.scoutingMissions.length === 0) return false;
    const roster = buildUnitRoster(state.config);
    let changed = false;
    state.scoutingMissions.forEach((mission) => {
        if (mission.status === 'traveling' || mission.status === 'returning') {
            const settlement = state.settlementsById[mission.fromSettlementId];
            const targetTileId =
                mission.status === 'returning' ? settlement?.tileId : mission.targetTileId;
            if (!targetTileId || !mission.currentTileId) return;

            if (mission.route.length === 0 && mission.routeIndex === 0) {
                const routeResult = buildScoutingRoute(model, mission.currentTileId, targetTileId);
                if (routeResult.stopReason) {
                    if (mission.status === 'returning' && mission.currentTileId === targetTileId) {
                        mission.status = 'complete';
                        mission.completedAt = state.simulation.tick;
                    } else {
                        mission.status = 'holding';
                    }
                    mission.progress = 0;
                    mission.travelTicks = 0;
                    changed = true;
                    return;
                }
                mission.route = routeResult.route;
                mission.routeIndex = 0;
                mission.travelTicks =
                    mission.startDelay + mission.route.length * Math.max(1, mission.stepTicks);
            }

            mission.progress += 1;
            if (mission.progress < mission.startDelay) return;
            const tickOffset = mission.progress - mission.startDelay;
            if (tickOffset % Math.max(1, mission.stepTicks) !== 0) return;

            if (mission.routeIndex >= mission.route.length) {
                mission.progress = 0;
                mission.routeIndex = 0;
                mission.route = [];
                if (mission.status === 'returning') {
                    mission.status = 'complete';
                    mission.completedAt = state.simulation.tick;
                    const weaponsToReturn = mission.units.reduce((sum, group) => {
                        const unit = roster.get(group.type);
                        if (!unit) return sum;
                        return sum + unit.weaponCost * group.count;
                    }, 0);
                    if (settlement) {
                        addInventory(settlement, 'weapons', weaponsToReturn);
                    }
                } else {
                    mission.status = 'holding';
                }
                changed = true;
                return;
            }

            const nextTileId = mission.route[mission.routeIndex];
            mission.routeIndex += 1;
            mission.currentTileId = nextTileId;
            if (!state.discoveredTileIds.has(nextTileId)) {
                state.discoveredTileIds.add(nextTileId);
                changed = true;
            }
            const enemy = state.enemyForces[nextTileId];
            if (enemy && !enemy.defeatedAt) {
                const previousStatus = mission.status;
                mission.status = 'battle';
                mission.resumeStatus = previousStatus === 'returning' ? 'returning' : 'traveling';
                mission.battle = createBattleState({
                    id: `battle_${mission.id}`,
                    tileId: nextTileId,
                    attacker: mission.units,
                    defender: enemy.units,
                    config: state.config.battle,
                    roster,
                    rngSeed: hashSeed(state.config.map.seed ?? 0, mission.id),
                });
                changed = true;
                return;
            }

            if (mission.routeIndex >= mission.route.length) {
                mission.progress = 0;
                mission.routeIndex = 0;
                mission.route = [];
                if (mission.status === 'returning') {
                    mission.status = 'complete';
                    mission.completedAt = state.simulation.tick;
                    const weaponsToReturn = mission.units.reduce((sum, group) => {
                        const unit = roster.get(group.type);
                        if (!unit) return sum;
                        return sum + unit.weaponCost * group.count;
                    }, 0);
                    if (settlement) {
                        addInventory(settlement, 'weapons', weaponsToReturn);
                    }
                } else {
                    mission.status = 'holding';
                }
                changed = true;
                return;
            }
            return;
        }

        if (mission.status === 'battle' && mission.battle) {
            if (advanceBattle(mission.battle, roster, state.config.battle)) {
                changed = true;
            }
            if (mission.battle.status !== 'complete') return;
            const reportId = `battle_report_${state.battleReportIdCounter + 1}`;
            state.battleReportIdCounter += 1;
            const report = buildBattleReport(
                mission.battle,
                roster,
                reportId,
                mission.createdAt,
                state.simulation.tick,
            );
            state.battleReports.push(report);
            mission.battleReportId = reportId;
            const enemy = state.enemyForces[mission.targetTileId];
            if (enemy) {
                enemy.units = report.defenderFinal.map((group) => ({ ...group }));
                if (report.outcome.winner === 'attacker') {
                    enemy.defeatedAt = state.simulation.tick;
                }
            }
            const attackerLosses = report.outcome.attackerLosses;
            if (attackerLosses > 0) {
                const settlement = state.settlementsById[mission.fromSettlementId];
                if (settlement) {
                    applyPopulationLoss(settlement, attackerLosses);
                }
                mission.units = report.attackerFinal.map((group) => ({ ...group }));
            }
            if (report.outcome.attackerRemaining > 0) {
                if (mission.resumeStatus === 'returning') {
                    if (mission.routeIndex < mission.route.length) {
                        mission.status = 'returning';
                    } else {
                        mission.status = 'complete';
                        mission.completedAt = state.simulation.tick;
                        const settlement = state.settlementsById[mission.fromSettlementId];
                        const weaponsToReturn = mission.units.reduce((sum, group) => {
                            const unit = roster.get(group.type);
                            if (!unit) return sum;
                            return sum + unit.weaponCost * group.count;
                        }, 0);
                        if (settlement) {
                            addInventory(settlement, 'weapons', weaponsToReturn);
                        }
                    }
                } else if (mission.routeIndex < mission.route.length) {
                    mission.status = 'traveling';
                } else {
                    mission.status = 'holding';
                }
            } else {
                mission.status = 'complete';
                mission.completedAt = state.simulation.tick;
            }
            mission.resumeStatus = undefined;
            changed = true;
            return;
        }
        if (mission.status === 'holding' && mission.plan.length > 0) {
            const nextTargetId = mission.plan.shift();
            if (!nextTargetId) return;
            const currentTile = model.tiles.find((tile) => tile.id === mission.currentTileId);
            const nextTile = model.tiles.find((tile) => tile.id === nextTargetId);
            if (currentTile && nextTile) {
                mission.targetTileId = nextTile.id;
                mission.distance = hexDistance(currentTile.position, nextTile.position);
                mission.travelTicks = getScoutingTravelTicks(
                    mission.distance,
                    state.config.scouting,
                );
                mission.progress = 0;
                mission.route = [];
                mission.routeIndex = 0;
                mission.status = 'traveling';
                changed = true;
            }
            return;
        }
    });

    return changed;
}

function buildScoutingRoute(
    model: MapModel,
    fromTileId: string,
    targetTileId: string,
): { route: string[]; stopReason?: 'water' | 'blocked' } {
    const fromTile = model.tiles.find((tile) => tile.id === fromTileId);
    const targetTile = model.tiles.find((tile) => tile.id === targetTileId);
    if (!fromTile || !targetTile) return { route: [], stopReason: 'blocked' };
    if (targetTile.biome.type === 'water') return { route: [], stopReason: 'water' };

    const visited = new Set<string>();
    const route: string[] = [];
    let current: Tile = fromTile;
    let guard = 0;
    while (current.id !== targetTile.id && guard < model.tiles.length) {
        guard += 1;
        visited.add(current.id);
        const next = findNextStep(model, current, targetTile, visited);
        if (!next) return { route, stopReason: 'blocked' };
        route.push(next.id);
        current = next;
    }
    return { route };
}

function findNextStep(
    model: MapModel,
    current: Tile,
    target: Tile,
    visited: Set<string>,
): Tile | null {
    const distanceNow = hexDistance(current.position, target.position);
    let best: { tile: Tile; distance: number } | null = null;
    const dirs = [
        { q: 1, r: 0, s: -1 },
        { q: 1, r: -1, s: 0 },
        { q: 0, r: -1, s: 1 },
        { q: -1, r: 0, s: 1 },
        { q: -1, r: 1, s: 0 },
        { q: 0, r: 1, s: -1 },
    ];
    for (const dir of dirs) {
        const nq = current.position.q + dir.q;
        const nr = current.position.r + dir.r;
        const ns = current.position.s + dir.s;
        const neighbor = model.getTileByCube(nq, nr, ns);
        if (!neighbor || neighbor.biome.type === 'water') continue;
        if (visited.has(neighbor.id)) continue;
        const dist = hexDistance(neighbor.position, target.position);
        if (dist >= distanceNow) continue;
        if (!best || dist < best.distance) {
            best = { tile: neighbor, distance: dist };
        }
    }
    return best ? best.tile : null;
}

function hashSeed(seed: number, id: string): number {
    let h = seed >>> 0;
    for (let i = 0; i < id.length; i += 1) {
        h ^= id.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}
